import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { Message } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { ChannelThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import { groupBy } from "@redotech/util/map";
import { useMemo } from "react";
import { useSingleSupportTicketData } from "./use-support-ticket-data";

export interface ThreadWithMessages {
  thread: ChannelThread;
  messages: Message[];
}

export function useSupportTicketView(supportTicketId: SupportTicketId) {
  const { supportTicket, threads, messages, isLoading } =
    useSingleSupportTicketData(supportTicketId);

  const messagesByThreadId = useMemo(
    () => groupBy(messages, (msg) => msg.channelThreadId),
    [messages],
  );

  const threadsWithMessages = useMemo(
    (): ThreadWithMessages[] =>
      threads.map((thread) => ({
        thread,
        messages: messagesByThreadId.get(thread.id) ?? [],
      })),
    [threads, messagesByThreadId],
  );

  const customerName = supportTicket?.customer?.displayName ?? "Customer";

  const latestMessageTime = useMemo(() => {
    if (messages.length === 0) {
      return null;
    }
    const sorted = [...messages].sort(
      (a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime(),
    );
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    return new Date(sorted[0].sentAt);
  }, [messages]);

  return {
    supportTicket,
    threads,
    threadsWithMessages,
    messages,
    isLoading,
    customerName,
    latestMessageTime,
  };
}
